import React from 'react';
import * as Sentry from '@sentry/react';

interface Props {
  children: React.ReactNode;
}

interface State {
  error: Error | null;
}

class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    if (process.env.REACT_APP_SENTRY_DSN) {
      Sentry.withScope((scope) => {
        scope.setExtras({ componentStack: info.componentStack });
        Sentry.captureException(error);
      });
    }
    // eslint-disable-next-line no-console
    console.error('Render error caught by boundary:', error, info);
  }

  handleReset = (): void => {
    this.setState({ error: null });
  };

  handleReload = (): void => {
    window.location.reload();
  };

  render(): React.ReactNode {
    if (!this.state.error) return this.props.children;

    const isDev = process.env.NODE_ENV === 'development';

    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <h1 style={styles.title}>Something went wrong</h1>
          <p style={styles.message}>
            The page ran into an unexpected error. Your work is saved — try reloading.
          </p>
          {isDev && (
            <pre style={styles.pre}>{String(this.state.error?.stack || this.state.error)}</pre>
          )}
          <div style={styles.row}>
            <button style={styles.primary} onClick={this.handleReload}>Reload page</button>
            <button style={styles.secondary} onClick={this.handleReset}>Try again</button>
          </div>
        </div>
      </div>
    );
  }
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    background: '#f5f6fa'
  },
  card: {
    maxWidth: 560,
    width: '100%',
    background: '#fff',
    borderRadius: 12,
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
    padding: 32
  },
  title: { margin: '0 0 12px', fontSize: 22, color: '#1a1a1a' },
  message: { margin: '0 0 20px', color: '#555', lineHeight: 1.5 },
  pre: {
    background: '#0f111a',
    color: '#e1e4e8',
    padding: 16,
    borderRadius: 8,
    fontSize: 12,
    overflow: 'auto',
    maxHeight: 240,
    margin: '0 0 20px'
  },
  row: { display: 'flex', gap: 12 },
  primary: {
    background: '#2563eb',
    color: '#fff',
    border: 'none',
    padding: '10px 18px',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 14
  },
  secondary: {
    background: 'transparent',
    color: '#2563eb',
    border: '1px solid #2563eb',
    padding: '10px 18px',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 14
  }
};

export default ErrorBoundary;
